import type { ValidationIssue, ValidationResult } from "@personnel/core";
import type { LeviathanCaptain } from "../character.js";
import {
  ATTRIBUTES,
  ATTRIBUTE_IDS,
  BALANCED_ATTRIBUTE_IDS,
  MAX_BALANCE_SPREAD,
  MAX_PURCHASABLE_RATING,
  MIN_RATING,
  isPurchasableRating,
} from "../data/attributes.js";
import { EXPERIENCE_LEVELS } from "../data/experience.js";
import { FACTIONS, MIN_TIER, PATH_CHOICE_RENOWN, renownRequiredForTier } from "@personnel/setting";
import { MAX_QUALITIES, qualityById } from "../data/qualities.js";
import { derive } from "./derive.js";

export function validate(captain: LeviathanCaptain): ValidationResult {
  const issues: ValidationIssue[] = [];
  const derived = derive(captain);

  // --- Identity ---------------------------------------------------------

  if (captain.name.trim() === "") {
    issues.push({
      severity: "error",
      code: "identity.name.missing",
      message: "Give your captain a name.",
      path: ["name"],
    });
  }

  // --- Build mode -------------------------------------------------------

  if (captain.buildMode === "level" && captain.experienceLevel === null) {
    issues.push({
      severity: "error",
      code: "experience.level.missing",
      message: "Choose an Experience Level, or switch to building by Cost.",
      path: ["experienceLevel"],
    });
  }

  // --- Attribute ratings ------------------------------------------------

  for (const id of ATTRIBUTE_IDS) {
    const rating = captain.attributes[id];
    if (!isPurchasableRating(rating)) {
      issues.push({
        severity: "error",
        code: "attribute.rating.outOfRange",
        message: `${ATTRIBUTES[id].name} must be a whole number between ${MIN_RATING} and ${MAX_PURCHASABLE_RATING}.`,
        path: ["attributes", id],
      });
    }
  }

  // --- Balance ----------------------------------------------------------
  //
  // Rank is excluded entirely: it is neither subject to the rule nor counted
  // when finding the lowest attribute.

  const spread = derived.highestBalanced - derived.lowestBalanced;
  if (spread > MAX_BALANCE_SPREAD) {
    const lagging = BALANCED_ATTRIBUTE_IDS.filter(
      (id) => captain.attributes[id] < derived.balanceFloor,
    );
    issues.push({
      severity: "error",
      code: "attribute.balance",
      message: `Attributes must stay within ${MAX_BALANCE_SPREAD} of each other. With a ${derived.highestBalanced}, every other attribute needs to be at least ${derived.balanceFloor} — raise ${lagging
        .map((id) => ATTRIBUTES[id].name)
        .join(", ")}.`,
      path: ["attributes"],
    });
  }

  // --- Maximum Rank -----------------------------------------------------
  //
  // Caps the purchased rating only. A Quality that grants a Rank is not a
  // purchase and is deliberately not measured against this.

  if (captain.attributes.rank > derived.maxRank) {
    issues.push({
      severity: "error",
      code: "attribute.rank.aboveMaximum",
      message: `Rank may not exceed the average of the other five attributes, rounded — at most ${derived.maxRank} here. Raise the others or lower Rank.`,
      path: ["attributes", "rank"],
    });
  }

  // --- Qualities --------------------------------------------------------

  const seen = new Set<string>();
  let countedAgainstLimit = 0;
  const tagCounts = new Map<string, string[]>();

  for (const id of captain.qualities) {
    const quality = qualityById(id);

    if (quality === undefined) {
      issues.push({
        severity: "error",
        code: "quality.unknown",
        message: `"${id}" is not a Leviathan Quality.`,
        path: ["qualities"],
      });
      continue;
    }

    if (seen.has(id)) {
      issues.push({
        severity: "error",
        code: "quality.duplicate",
        message: `${quality.name} is selected more than once.`,
        path: ["qualities"],
      });
      continue;
    }
    seen.add(id);

    if (quality.exemptFromLimit !== true) {
      countedAgainstLimit += 1;
    }

    if (quality.tag !== null) {
      tagCounts.set(quality.tag, [...(tagCounts.get(quality.tag) ?? []), quality.name]);
    }

    for (const requirement of quality.requires ?? []) {
      if (requirement.unmodelled !== undefined) {
        issues.push({
          severity: "warning",
          code: "quality.requirement.unverified",
          message: `${quality.name} requires ${requirement.unmodelled}, which this tool cannot check yet — confirm it yourself.`,
          path: ["qualities"],
        });
        continue;
      }

      if (requirement.reputationTier !== undefined) {
        if (derived.reputationTier < requirement.reputationTier) {
          const needed = renownRequiredForTier(requirement.reputationTier);
          issues.push({
            severity: "error",
            code: "quality.requirement.reputation",
            message: `${quality.name} requires Reputation Tier ${requirement.reputationTier} (${needed}+ Renown). This captain is Tier ${derived.reputationTier} on ${captain.renown} Renown.`,
            path: ["renown"],
          });
        }
      }

      if (requirement.attribute === "rank" && requirement.minimum !== undefined) {
        if (captain.attributes.rank < requirement.minimum) {
          issues.push({
            severity: "error",
            code: "quality.requirement.rank",
            message: `${quality.name} requires Rank ${requirement.minimum} or higher.`,
            path: ["qualities"],
          });
        }
      }

      if (requirement.faction !== undefined && captain.faction !== requirement.faction) {
        issues.push({
          severity: "error",
          code: "quality.requirement.faction",
          message: `${quality.name} is only available to the ${FACTIONS[requirement.faction].name}.`,
          path: ["faction"],
        });
      }
    }
  }

  if (countedAgainstLimit > MAX_QUALITIES) {
    issues.push({
      severity: "error",
      code: "quality.tooMany",
      message: `A captain may have at most ${MAX_QUALITIES} Qualities. Remove ${countedAgainstLimit - MAX_QUALITIES}.`,
      path: ["qualities"],
    });
  }

  for (const [tag, names] of tagCounts) {
    if (names.length > 1) {
      const label = tag.charAt(0).toUpperCase() + tag.slice(1);
      issues.push({
        severity: "error",
        code: "quality.tagConflict",
        message: `A captain may only have one ${label} Quality — ${names.join(" and ")} conflict.`,
        path: ["qualities"],
      });
    }
  }

  // --- Essence ----------------------------------------------------------

  if (!Number.isInteger(captain.essence) || captain.essence < 0) {
    issues.push({
      severity: "error",
      code: "essence.invalid",
      message: "Essence must be a whole number of points, zero or more.",
      path: ["essence"],
    });
  }

  if (!Number.isInteger(captain.essenceUsed) || captain.essenceUsed < 0) {
    issues.push({
      severity: "error",
      code: "essence.used.invalid",
      message: "Essence Used must be a whole number of points, zero or more.",
      path: ["essenceUsed"],
    });
  } else if (captain.essenceUsed > captain.essence) {
    // Play tracking, not a creation rule, so this is advisory -- it should not
    // block a captain who is otherwise legal.
    issues.push({
      severity: "warning",
      code: "essence.used.exceedsHeld",
      message: `${captain.essenceUsed} Essence marked used, but only ${captain.essence} bought.`,
      path: ["essenceUsed"],
    });
  }

  // --- Renown and Reputation --------------------------------------------

  if (!Number.isInteger(captain.renown) || captain.renown < 0) {
    issues.push({
      severity: "error",
      code: "renown.invalid",
      message: "Renown must be a whole number, zero or more.",
      path: ["renown"],
    });
  }

  // A path is chosen on reaching Tier 2. Below that every path reads the same,
  // so one is neither needed nor meaningful.
  if (derived.reputationTier > MIN_TIER && captain.reputationPath === null) {
    issues.push({
      severity: "warning",
      code: "reputation.pathMissing",
      message: `A captain with ${PATH_CHOICE_RENOWN}+ Renown chooses a Reputation Path.`,
      path: ["reputationPath"],
    });
  }

  // --- Experience budget ------------------------------------------------
  //
  // Both bounded modes measure against everything committed, Essence included.
  // They differ in what unspent Experience means: a level's allowance describes
  // the captain entering play, so leftovers are forfeited, while a points pool
  // is carried and its leftovers stay available.

  if (captain.buildMode === "level" && captain.experienceLevel === null) {
    // Already reported under build mode; nothing further to check.
  } else if (derived.experienceAvailable !== null) {
    const available = derived.experienceAvailable;
    const spent = derived.experienceCommitted;
    const label =
      captain.buildMode === "level" && captain.experienceLevel !== null
        ? EXPERIENCE_LEVELS[captain.experienceLevel].name
        : "This pool";

    if (spent > available) {
      issues.push({
        severity: "error",
        code: "experience.overspent",
        message: `${label} allows ${available} Experience; this captain has committed ${spent}. Reduce by ${spent - available}.`,
        path: [captain.buildMode === "level" ? "experienceLevel" : "experiencePoints"],
      });
    } else if (spent < available) {
      const remaining = available - spent;
      issues.push({
        severity: "warning",
        code: derived.unspentIsLost ? "experience.unspent" : "experience.available",
        message: derived.unspentIsLost
          ? `${remaining} Experience unspent. An Experience Level is the captain entering play, so this is forfeited rather than carried.`
          : `${remaining} Experience still available to spend.`,
        path: ["experienceLevel"],
      });
    }
  }

  if (captain.buildMode === "points" && captain.experiencePoints === null) {
    issues.push({
      severity: "error",
      code: "experience.points.missing",
      message: "Enter the Experience Point total you are working with, or switch build mode.",
      path: ["experiencePoints"],
    });
  }

  // The minimum Rank belongs to the Experience Level itself, so it applies in
  // that mode alone -- a points pool carries no such floor.
  if (captain.buildMode === "level" && captain.experienceLevel !== null) {
    const level = EXPERIENCE_LEVELS[captain.experienceLevel];
    if (captain.attributes.rank < level.minimumRank) {
      issues.push({
        severity: "error",
        code: "experience.minimumRank",
        message: `${level.name} captains need at least Rank ${level.minimumRank}.`,
        path: ["attributes", "rank"],
      });
    }
  }

  return { issues };
}
